import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { compressImageToDataURL } from "@/lib/image";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ExternalLink, GalleryHorizontal, Check, ImageOff, Handshake, Upload, X, MousePointerClick, GripVertical, ArrowUp, ArrowDown, Inbox, AlertTriangle, CalendarClock } from "lucide-react";

const emptyForm = {
  name: "",
  logo_url: "",
  website_url: "",
  description: "",
  tier: "",
  sort_order: 0,
  active: true,
  contract_start_at: "",
  contract_end_at: "",
};
const MAX_LOGO_BYTES = 3 * 1024 * 1024; // 3MB — logos are small assets

// datetime-local helpers — convert between the picker's local naïve
// string and the UTC ISO the backend stores.
const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const toISO = (localStr) => {
  if (!localStr) return "";
  try { return new Date(localStr).toISOString(); } catch { return ""; }
};

// Compute a status string for the list view based on active + contract window.
function contractStatus(s) {
  if (!s.active) return "hidden";
  const now = Date.now();
  const start = s.contract_start_at ? new Date(s.contract_start_at).getTime() : null;
  const end = s.contract_end_at ? new Date(s.contract_end_at).getTime() : null;
  if (start && now < start) return "upcoming";
  if (end && now >= end) return "expired";
  return "live";
}

/**
 * Admin panel for managing sponsors that appear on the landing sponsor
 * strip and in the members portal Sponsors page. Each sponsor has a
 * name, logo (picked from the club gallery), a website URL for
 * click-through, an optional short description + tier, a sort order for
 * strip position, and an active toggle to hide without deleting.
 */
export default function AdminSponsors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // sponsor or null when adding
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sponsors");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sponsors");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Deep-link ``/a/sponsors?edit=<id>`` auto-opens the matching sponsor's
  // edit modal — used by the "Convert to sponsor" flow in the inbox so
  // the admin lands directly on logo/website fields after promotion.
  useEffect(() => {
    if (loading || !items.length) return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (!editId) return;
    const found = items.find((r) => r.id === editId);
    if (found) {
      openEdit(found);
      // Strip the param so a manual close+reopen doesn't fire this again.
      const next = new URL(window.location.href);
      next.searchParams.delete("edit");
      window.history.replaceState({}, "", next.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      logo_url: s.logo_url || "",
      website_url: s.website_url || "",
      description: s.description || "",
      tier: s.tier || "",
      sort_order: s.sort_order || 0,
      active: s.active !== false,
      contract_start_at: toLocalInput(s.contract_start_at),
      contract_end_at: toLocalInput(s.contract_end_at),
    });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Sponsor name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        logo_url: (form.logo_url || "").trim(),
        website_url: (form.website_url || "").trim(),
        description: (form.description || "").trim() || null,
        tier: (form.tier || "").trim() || null,
        sort_order: Number(form.sort_order) || 0,
        contract_start_at: toISO(form.contract_start_at) || null,
        contract_end_at: toISO(form.contract_end_at) || null,
      };
      if (payload.contract_start_at && payload.contract_end_at && payload.contract_end_at <= payload.contract_start_at) {
        toast.error("Contract end must be after the start date");
        setSaving(false);
        return;
      }
      if (editing) {
        await api.put(`/sponsors/${editing.id}`, payload);
        toast.success("Sponsor updated");
      } else {
        await api.post("/sponsors", payload);
        toast.success("Sponsor added");
      }
      closeDialog();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sponsor");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sponsors/${id}`);
      toast.success("Sponsor deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  // ---- Drag-drop / arrow-based reordering ----
  // Persist the new order to the backend. Uses the ``ids[]`` bulk endpoint
  // which rewrites sort_order in one pass. Optimistically applies the
  // reorder locally first so the list snaps to the new position without
  // waiting on the network.
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const persistOrder = async (nextItems) => {
    setItems(nextItems.map((s, i) => ({ ...s, sort_order: i * 10 })));
    try {
      await api.post("/sponsors/reorder", { ids: nextItems.map((s) => s.id) });
    } catch (e) {
      toast.error("Could not save new order — reloading");
      load();
    }
  };
  const moveBy = (id, delta) => {
    const cur = [...sorted];
    const idx = cur.findIndex((s) => s.id === id);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= cur.length) return;
    const [x] = cur.splice(idx, 1);
    cur.splice(to, 0, x);
    persistOrder(cur);
  };
  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch {}
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDrop = (id) => (e) => {
    e.preventDefault();
    const from = dragId;
    setDragId(null); setDragOverId(null);
    if (!from || from === id) return;
    const cur = [...sorted];
    const fromIdx = cur.findIndex((s) => s.id === from);
    const toIdx = cur.findIndex((s) => s.id === id);
    if (fromIdx < 0 || toIdx < 0) return;
    const [x] = cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, x);
    persistOrder(cur);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  const pickFromGallery = (url) => { set("logo_url", url); setGalleryOpen(false); };

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items]
  );

  return (
    <AppShell variant="admin">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-sponsors-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
              Admin · Sponsors
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Handshake className="h-7 w-7 text-[hsl(var(--accent))]" /> Sponsor partners
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Logos here appear on the public landing sponsor strip and inside the members portal Sponsors page. Clicking a logo opens the sponsor's website in a new tab.
            </p>
          </div>
          <Button onClick={openNew} className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90" data-testid="sponsor-new-btn">
            <Plus className="h-4 w-4 mr-1" /> Add sponsor
          </Button>
        </div>

        <Card className="p-4 rounded-2xl flex items-center gap-3 flex-wrap" data-testid="sponsors-proposal-shortcut">
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Sponsorship proposal page</div>
            <div className="text-xs text-muted-foreground">Edit the public /sponsor page shown when visitors tap "Become a sponsor" on the landing.</div>
          </div>
          <a href="/a/sponsors/proposal" data-testid="sponsors-proposal-open">
            <Button variant="outline" className="rounded-full">Open editor</Button>
          </a>
        </Card>

        <SubmissionsShortcut />

        <RenewalsShortcut onOpenEdit={openEdit} sponsors={items} />

        {loading ? (
          <Card className="p-14 text-center text-muted-foreground rounded-2xl">Loading sponsors…</Card>
        ) : sorted.length === 0 ? (
          <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl" data-testid="sponsors-empty">
            <Handshake className="h-8 w-8 mx-auto mb-3 opacity-60" />
            No sponsors yet. Tap "Add sponsor" to create the first partner card.
          </Card>
        ) : (
          <div className="grid gap-3">
            <p className="text-xs text-muted-foreground -mb-1" data-testid="sponsor-reorder-hint">
              Drag any row by the handle, or use the ↑/↓ arrows, to reorder how sponsors appear on the landing strip.
            </p>
            {sorted.map((s, idx) => (
              <Card
                key={s.id}
                className={`p-4 rounded-2xl flex items-center gap-4 flex-wrap transition-all ${dragId === s.id ? "opacity-40" : ""} ${dragOverId === s.id && dragId !== s.id ? "ring-2 ring-[hsl(var(--accent))]" : ""}`}
                data-testid={`sponsor-row-${s.id}`}
                draggable
                onDragStart={onDragStart(s.id)}
                onDragOver={onDragOver(s.id)}
                onDrop={onDrop(s.id)}
                onDragEnd={onDragEnd}
              >
                {/* Drag handle + up/down for mobile / keyboard */}
                <div className="flex flex-col items-center gap-0.5 shrink-0 -my-1">
                  <button
                    type="button"
                    onClick={() => moveBy(s.id, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                    className="h-6 w-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none rounded"
                    data-testid={`sponsor-move-up-${s.id}`}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <div
                    className="h-6 w-6 inline-flex items-center justify-center text-muted-foreground cursor-grab active:cursor-grabbing"
                    title="Drag to reorder"
                    aria-label="Drag handle"
                    data-testid={`sponsor-drag-handle-${s.id}`}
                  >
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <button
                    type="button"
                    onClick={() => moveBy(s.id, 1)}
                    disabled={idx === sorted.length - 1}
                    aria-label="Move down"
                    className="h-6 w-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none rounded"
                    data-testid={`sponsor-move-down-${s.id}`}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="h-16 w-24 shrink-0 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <ImageOff className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold truncate">{s.name}</div>
                    {s.tier && <Badge variant="outline" className="text-[10px]">{s.tier}</Badge>}
                    {(() => {
                      const st = contractStatus(s);
                      if (st === "hidden") return <Badge variant="outline" className="text-[10px]">Hidden</Badge>;
                      if (st === "upcoming") return <Badge variant="outline" className="text-[10px] border-amber-500/60 text-amber-600">Upcoming</Badge>;
                      if (st === "expired") return <Badge variant="outline" className="text-[10px] border-red-500/60 text-red-600">Expired</Badge>;
                      return <Badge variant="outline" className="text-[10px] border-emerald-500/60 text-emerald-700">Live</Badge>;
                    })()}
                    <RenewalChip end={s.contract_end_at} />
                    <span className="text-[10px] text-muted-foreground font-mono">#{idx + 1}</span>
                    <Badge
                      variant="outline"
                      className="text-[10px] gap-1 border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))]"
                      title={s.last_clicked_at ? `Last click ${new Date(s.last_clicked_at).toLocaleString()}` : "No clicks yet"}
                      data-testid={`sponsor-clicks-${s.id}`}
                    >
                      <MousePointerClick className="h-3 w-3" />
                      {(s.click_count || 0).toLocaleString()} click{(s.click_count || 0) === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  {(s.contract_start_at || s.contract_end_at) && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {s.contract_start_at ? new Date(s.contract_start_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "no start"}
                      {" → "}
                      {s.contract_end_at ? new Date(s.contract_end_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "no end"}
                    </div>
                  )}
                  {s.website_url && (
                    <a
                      href={s.website_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-xs text-[hsl(var(--accent))] hover:underline inline-flex items-center gap-1 mt-0.5"
                      data-testid={`sponsor-link-${s.id}`}
                    >
                      {s.website_url} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {s.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(s)} data-testid={`sponsor-edit-${s.id}`}>
                    <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => setConfirmDelete(s)} data-testid={`sponsor-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add / edit dialog — a single dialog that flips its copy based
          on whether we're editing an existing sponsor. */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit sponsor" : "Add sponsor"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the sponsor logo, link, or visibility."
                : "Add a new partner to the sponsor strip."}
            </DialogDescription>
          </DialogHeader>
          <SponsorForm form={form} set={set} onPickGallery={() => setGalleryOpen(true)} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeDialog} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90"
              data-testid="sponsor-save-btn"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add sponsor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gallery picker */}
      <GalleryPickerDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        currentUrl={form.logo_url}
        onSelect={pickFromGallery}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sponsor?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.name} will be removed from every surface. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => remove(confirmDelete.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function SponsorForm({ form, set, onPickGallery }) {
  const fileRef = useRef(null);

  // Read the chosen file as a base64 data URL and stash it directly into
  // ``form.logo_url``. Matches the pattern used by Events so the
  // backend receives the same string shape whether the admin uploaded
  // from device, pasted a URL, or picked from the gallery.
  //
  // Side-effect: once the compressed data URL is in hand, we also push
  // it into the club gallery (auto-approved) so other admins can reuse
  // the same logo via the "Pick from gallery" picker without having to
  // re-upload it.
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Logo must be 10MB or smaller"); return; }
    // Validate exact dimensions: 208x130px only
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth !== 208 || img.naturalHeight !== 130) {
        toast.error(`Logo must be exactly 208×130px (yours is ${img.naturalWidth}×${img.naturalHeight}px)`);
        URL.revokeObjectURL(img.src);
        return;
      }
      URL.revokeObjectURL(img.src);
      // Read file as-is — no compression
      const reader = new FileReader();
      reader.onload = (re) => {
        const dataUrl = re.target.result;
        set("logo_url", dataUrl);
        const sizeKB = Math.round(f.size / 1024);
        toast.success(`Logo set (${sizeKB}KB, 208×130px)`);
        // Fire-and-forget gallery registration
        (async () => {
          try {
            const title = (form?.name && form.name.trim()) || f.name.replace(/\.[^.]+$/, "") || "Sponsor logo";
            const { data } = await api.post("/gallery", {
              title,
              description: "Uploaded from the Sponsors panel",
              image_url: dataUrl,
              tags: ["sponsor-logo"],
            });
            if (data?.id) {
              await api.post(`/gallery/${data.id}/approve`).catch(() => {});
            }
          } catch { /* silent — sponsor save still succeeds */ }
        })();
      };
      reader.readAsDataURL(f);
    };
    img.src = URL.createObjectURL(f);
  };

  const clearLogo = () => {
    set("logo_url", "");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-medium">Name *</Label>
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Kasana Financial" data-testid="sponsor-name-input" />
      </div>
      <div>
        <Label className="text-xs font-medium">Logo</Label>
        <div className="flex items-center gap-3 mt-1">
          <div className="relative h-16 w-24 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden shrink-0">
            {form.logo_url ? (
              <>
                <img src={form.logo_url} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                <button
                  type="button"
                  onClick={clearLogo}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  aria-label="Remove logo"
                  data-testid="sponsor-logo-clear-btn"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : (
              <ImageOff className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,.png"
              onChange={onFile}
              className="hidden"
              data-testid="sponsor-logo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-8"
              onClick={() => fileRef.current?.click()}
              data-testid="sponsor-logo-upload-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1" /> Upload from device
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-8"
              onClick={onPickGallery}
              data-testid="sponsor-gallery-picker-btn"
            >
              <GalleryHorizontal className="h-3.5 w-3.5 mr-1" /> Pick from gallery
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          PNG, JPG, SVG or WebP · must be exactly 208×130px · no compression applied
        </p>
      </div>
      <div>
        <Label className="text-xs font-medium">Website URL</Label>
        <Input
          type="url"
          value={form.website_url}
          onChange={(e) => set("website_url", e.target.value)}
          placeholder="https://sponsor.example.com"
          data-testid="sponsor-website-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Tier (optional)</Label>
          <Input value={form.tier} onChange={(e) => set("tier", e.target.value)} placeholder="Platinum · Gold · Silver" data-testid="sponsor-tier-input" />
        </div>
        <div>
          <Label className="text-xs font-medium">Website URL is opened in a new tab</Label>
          <p className="text-[11px] text-muted-foreground mt-2">
            Reorder sponsors by dragging them on the list — you no longer need to type a sort number.
          </p>
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium">Description (optional)</Label>
        <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Shown on the member portal Sponsors page." data-testid="sponsor-description-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Contract start (optional)</Label>
          <Input
            type="datetime-local"
            value={form.contract_start_at}
            onChange={(e) => set("contract_start_at", e.target.value)}
            data-testid="sponsor-contract-start-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Sponsor stays hidden until this moment. Leave blank to show immediately.</p>
        </div>
        <div>
          <Label className="text-xs font-medium">Contract end (optional)</Label>
          <Input
            type="datetime-local"
            value={form.contract_end_at}
            onChange={(e) => set("contract_end_at", e.target.value)}
            data-testid="sponsor-contract-end-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Sponsor auto-hides after this moment. Leave blank to run indefinitely.</p>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-[hsl(var(--secondary))] p-3">
        <div>
          <div className="text-sm font-semibold">Active</div>
          <div className="text-xs text-muted-foreground">Hide temporarily by turning this off without deleting.</div>
        </div>
        <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="sponsor-active-switch" />
      </div>
    </div>
  );
}

// Gallery picker (mirrors the Events panel version so the two panels stay
// visually consistent). Only "approved" gallery images are shown.
function GalleryPickerDialog({ open, onOpenChange, currentUrl, onSelect }) {
  const [photos, setPhotos] = useState(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/gallery", { params: { status: "approved" } });
        if (alive) setPhotos(data || []);
      } catch {
        if (alive) setPhotos([]);
      }
    })();
    return () => { alive = false; };
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl">
        <DialogHeader><DialogTitle>Pick a logo from the club gallery</DialogTitle></DialogHeader>
        {photos === null ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Loading gallery…</div>
        ) : photos.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">
            No approved gallery photos yet. Upload a logo via the Gallery panel first.
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sponsor-gallery-grid">
              {photos.map((g) => {
                const isSelected = currentUrl && currentUrl === g.image_url;
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => onSelect(g.image_url)}
                    className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${isSelected ? "border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/40" : "border-transparent hover:border-[hsl(var(--accent))]/60"}`}
                    data-testid={`sponsor-gallery-photo-${g.id}`}
                  >
                    <img src={g.image_url} alt={g.title || "Gallery photo"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    {isSelected && (
                      <div className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shadow-md">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}


/**
 * Shortcut card that surfaces the submissions inbox with a live count
 * of "new" enquiries so admins can see at a glance when something needs
 * attention. Fetches once on mount — no polling, refreshed manually via
 * the inbox itself.
 */
function SubmissionsShortcut() {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sponsorship-submissions");
        if (alive) setCounts(data.counts || {});
      } catch { /* silent — shortcut is optional */ }
    })();
    return () => { alive = false; };
  }, []);
  const newCount = counts?.new || 0;
  const allCount = counts?.all || 0;
  return (
    <Card className="p-4 rounded-2xl flex items-center gap-3 flex-wrap" data-testid="sponsors-submissions-shortcut">
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2">
          <Inbox className="h-4 w-4 text-[hsl(var(--accent))]" />
          Sponsorship enquiries inbox
          {newCount > 0 && (
            <Badge className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 text-[10px]">
              {newCount} new
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {allCount === 0
            ? "Nothing yet. Enquiries submitted via the public /sponsor form will appear here."
            : `${allCount} total enquiry${allCount === 1 ? "" : "s"} stored — tag, reply and archive from the inbox.`}
        </div>
      </div>
      <a href="/a/sponsors/submissions" data-testid="sponsors-submissions-open">
        <Button variant="outline" className="rounded-full">
          Open inbox {newCount > 0 ? `(${newCount})` : ""}
        </Button>
      </a>
    </Card>
  );
}


/* -------------------------------------------------------------------------- *
 *  Renewals surfaces — a warning card that groups sponsors by how imminent
 *  their contract expiry is (expired / urgent <30d / soon <60d) so admins
 *  never lose a partner to a silent contract lapse, and a tiny per-row
 *  chip that shows the countdown right next to the sponsor name.
 * -------------------------------------------------------------------------- */
function _daysUntil(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86_400_000);
}

function RenewalChip({ end }) {
  const days = _daysUntil(end);
  if (days === null) return null;
  // Only render an alert chip once we're inside the renewal horizon —
  // healthy multi-year contracts don't need visual noise.
  if (days > 60) return null;
  if (days < 0) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-red-500/60 text-red-600" title={`Contract ended ${new Date(end).toLocaleDateString()}`}>
        <AlertTriangle className="h-3 w-3" /> Expired {Math.abs(days)}d ago
      </Badge>
    );
  }
  const urgent = days <= 30;
  const color = urgent ? "border-red-500/60 text-red-600" : "border-amber-500/60 text-amber-700";
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${color}`} title={`Renews ${new Date(end).toLocaleDateString()}`}>
      <CalendarClock className="h-3 w-3" /> {days === 0 ? "Expires today" : `Expires in ${days}d`}
    </Badge>
  );
}

function RenewalsShortcut({ onOpenEdit, sponsors }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sponsors/renewals");
        if (alive) setData(data);
      } catch { /* silent — the shortcut is optional context */ }
    })();
    return () => { alive = false; };
    // Refetch whenever the sponsor list changes (renewal via edit dialog).
  }, [sponsors]);

  const total = data?.counts?.total || 0;
  if (!data) return null;
  if (total === 0) {
    return (
      <Card className="p-4 rounded-2xl flex items-center gap-3 flex-wrap border-emerald-500/30 bg-emerald-500/5" data-testid="sponsors-renewals-shortcut-ok">
        <CalendarClock className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">All sponsor contracts healthy</div>
          <div className="text-xs text-muted-foreground">No renewals due in the next {data.horizon_days} days. Nothing needs your attention right now.</div>
        </div>
      </Card>
    );
  }

  // Merge buckets in "worst first" order so admins tackle expired rows before soon.
  const items = [
    ...(data.expired || []).map((s) => ({ ...s, _bucket: "expired" })),
    ...(data.urgent || []).map((s) => ({ ...s, _bucket: "urgent" })),
    ...(data.soon || []).map((s) => ({ ...s, _bucket: "soon" })),
  ];

  return (
    <Card className="p-4 rounded-2xl border-amber-500/40 bg-amber-500/5 space-y-3" data-testid="sponsors-renewals-shortcut">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
            {total} sponsor{total === 1 ? "" : "s"} need attention
            {data.counts.expired > 0 && (
              <Badge className="rounded-full bg-red-600 hover:bg-red-700 text-white text-[10px]">
                {data.counts.expired} expired
              </Badge>
            )}
            {data.counts.urgent > 0 && (
              <Badge className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-[10px]">
                {data.counts.urgent} &lt; 30 days
              </Badge>
            )}
            {data.counts.soon > 0 && (
              <Badge variant="outline" className="rounded-full text-[10px] border-amber-500/60 text-amber-700">
                {data.counts.soon} within 60 days
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Renew or archive these partners so they keep appearing on the landing sponsor strip.
          </div>
        </div>
      </div>
      <ul className="divide-y divide-amber-500/20 -mx-1 rounded-lg overflow-hidden">
        {items.map((s) => {
          const days = _daysUntil(s.contract_end_at);
          const label = days === null ? "" : days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? "Expires today" : `Expires in ${days}d`;
          const color = s._bucket === "expired" ? "text-red-600" : s._bucket === "urgent" ? "text-red-600" : "text-amber-700";
          return (
            <li key={s.id} className="flex items-center gap-3 px-2 py-2 flex-wrap" data-testid={`sponsor-renewal-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{s.name}</div>
                <div className={`text-[11px] ${color} font-medium`}>{label} · ends {new Date(s.contract_end_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full h-8"
                onClick={() => onOpenEdit(s)}
                data-testid={`sponsor-renewal-open-${s.id}`}
              >
                Renew now
              </Button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
